import { useEffect, useState, useCallback } from "react";
import { showSubframeToast } from "../components/common/CasePrepToast";
import { DriveEntry, PickedItem, flattenDriveTree } from "./pickerTraversal";

const GOOGLE_CLIENT_ID = import.meta.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID;
const GOOGLE_APP_ID = import.meta.env.REACT_APP_GOOGLE_APP_ID;

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

const SUPPORTED_MIME_TYPES =
  "application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Lists a Drive folder's children, following `nextPageToken` recursively. */
const listDriveChildren = async (
  folderId: string,
  accessToken: string,
  pageToken?: string
): Promise<DriveEntry[]> => {
  const params = new URLSearchParams({
    q: `'${folderId}' in parents and trashed = false`,
    fields: "nextPageToken, files(id, name, mimeType)",
    pageSize: "1000",
    supportsAllDrives: "true",
    includeItemsFromAllDrives: "true",
    corpora: "allDrives"
  });
  if (pageToken) params.set("pageToken", pageToken);

  const response = await fetch(
    `https://www.googleapis.com/drive/v3/files?${params.toString()}`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  if (!response.ok) {
    throw new Error("Unable to list Google Drive folder contents");
  }

  const data = (await response.json()) as {
    files?: DriveEntry[];
    nextPageToken?: string;
  };
  const files = data.files ?? [];
  if (data.nextPageToken) {
    const rest = await listDriveChildren(
      folderId,
      accessToken,
      data.nextPageToken
    );
    return [...files, ...rest];
  }
  return files;
};

const downloadDriveFile = async (
  entry: DriveEntry,
  accessToken: string
): Promise<File | null> => {
  const response = await fetch(
    `https://www.googleapis.com/drive/v3/files/${entry.id}?alt=media&supportsAllDrives=true`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  if (!response.ok) return null;
  const blob = await response.blob();
  return new File([blob], entry.name, { type: entry.mimeType });
};

const pickerReady = (): boolean =>
  typeof window !== "undefined" && Boolean(window.google?.picker);

/** One load of the picker for the page, shared by every mounted uploader. */
let pickerLoad: Promise<void> | null = null;

const loadPicker = (): Promise<void> => {
  if (pickerReady()) return Promise.resolve();
  if (!pickerLoad) {
    pickerLoad = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://apis.google.com/js/api.js";
      script.onload = () => window.gapi.load("picker", () => resolve());
      script.onerror = () => reject(new Error("Google API failed to load"));
      document.body.appendChild(script);
    }).catch((error: unknown) => {
      pickerLoad = null;
      throw error;
    });
  }
  return pickerLoad;
};

export const useGoogleDrive = () => {
  const [isLoaded, setIsLoaded] = useState(pickerReady);

  useEffect(() => {
    if (isLoaded) return undefined;
    let live = true;
    loadPicker().then(
      () => {
        if (live) setIsLoaded(true);
      },
      () => {
        if (!live) return;
        showSubframeToast({
          title: "Failed to load Google Drive",
          variant: "error",
          icon: "FeatherAlertCircle",
          durationMs: 3000
        });
      }
    );
    return () => {
      live = false;
    };
  }, [isLoaded]);

  const pickCollected = useCallback(
    async ({ folders }: { folders: boolean }): Promise<PickedItem[]> => {
      if (!isLoaded) await loadPicker();

      return new Promise((resolve, reject) => {
        const showPicker = (accessToken: string) => {
          // Create view for My Drive. When folders are pickable we keep them
          // selectable; otherwise they are navigation-only as before.
          const myDriveView = new window.google.picker.DocsView()
            .setIncludeFolders(true)
            .setSelectFolderEnabled(folders)
            .setMimeTypes(SUPPORTED_MIME_TYPES)
            .setParent("root"); // Start from root of My Drive

          // Create view for Shared Drives
          const sharedDrivesView = new window.google.picker.DocsView(
            window.google.picker.ViewId.DOCS
          )
            .setMimeTypes(SUPPORTED_MIME_TYPES)
            .setEnableDrives(true)
            .setIncludeFolders(true)
            .setSelectFolderEnabled(folders);

          const picker = new window.google.picker.PickerBuilder()
            .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
            .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
            .enableFeature(window.google.picker.Feature.SUPPORT_TEAM_DRIVES)
            .setOAuthToken(accessToken)
            .setAppId(GOOGLE_APP_ID)
            .addView(myDriveView)
            .addView(sharedDrivesView)
            .setCallback(async (data: any) => {
              if (
                data[window.google.picker.Response.ACTION] ===
                window.google.picker.Action.PICKED
              ) {
                const docs = (data[window.google.picker.Response.DOCUMENTS] ??
                  []) as Array<{
                  id: string;
                  name: string;
                  mimeType: string;
                }>;

                // Show loading toast
                showSubframeToast({
                  title: `Retrieving ${docs.length} item${
                    docs.length === 1 ? "" : "s"
                  } from Google Drive...`,
                  variant: "neutral",
                  icon: "FeatherLoader",
                  durationMs: 1500
                });

                try {
                  const roots: DriveEntry[] = docs.map((doc) => ({
                    id: doc.id,
                    name: doc.name,
                    mimeType: doc.mimeType
                  }));
                  const items = await flattenDriveTree(
                    roots,
                    (folderId) => listDriveChildren(folderId, accessToken),
                    (entry) => downloadDriveFile(entry, accessToken)
                  );

                  showSubframeToast({
                    title: `Successfully retrieved ${items.length} file${
                      items.length === 1 ? "" : "s"
                    } from Google Drive`,
                    variant: "success",
                    icon: "FeatherCheck",
                    durationMs: 3000
                  });

                  resolve(items);
                } catch (error) {
                  showSubframeToast({
                    title: "Error retrieving files from Google Drive",
                    description:
                      error instanceof Error
                        ? error.message
                        : "Unknown error occurred",
                    variant: "error",
                    icon: "FeatherAlertCircle",
                    durationMs: 5000
                  });

                  reject(error);
                }
              } else if (
                data[window.google.picker.Response.ACTION] ===
                window.google.picker.Action.CANCEL
              ) {
                resolve([]);
              }
            })
            .build();
          picker.setVisible(true);
        };

        // Load the Google Identity Services library
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.onload = () => {
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: "https://www.googleapis.com/auth/drive.file",
            callback: (response: any) => {
              if (response.error) {
                reject(response.error);
                return;
              }
              showPicker(response.access_token);
            }
          });
          tokenClient.requestAccessToken();
        };
        document.body.appendChild(script);
      });
    },
    [isLoaded]
  );

  // Files only, flattened, for callers that don't preserve folder structure.
  const pickFiles = useCallback(
    async (): Promise<File[]> =>
      (await pickCollected({ folders: false })).map((item) => item.file),
    [pickCollected]
  );

  // Files and folders, keeping each file's relative path.
  const pickItems = useCallback(
    (): Promise<PickedItem[]> => pickCollected({ folders: true }),
    [pickCollected]
  );

  return {
    pickFiles,
    pickItems,
    isLoaded,
    isLoadingApi: !isLoaded
  };
};
